import React, { Fragment } from 'react';
import II18 from '../translate/II18';
import {
  Block,
  BlockHeader,
  Icon,
  Button,
  Fab,
  Gauge,
  Panel,
  Page,
  Row,
  Col
} from 'framework7-react';
import PropTypes from 'prop-types';
import {
  DOC_TYPE_FORM,
  DOC_TYPE_ID_CHECK,
  DOC_TYPE_IMMEDIATE_CONFIRMATION,
  DOC_TYPE_INFORMATION,
  DOC_TYPE_PAYMENT,
  DOC_TYPE_PDF_FORM,
  getIcon,
  isDocumentHasProblems,
  prepareIdCheckSteps
} from '../helpers/utils';
import { getDocumentTypeIcon } from '../helpers/documentType_icons';
import { gaeUser } from '../helpers/Analytics';

export function RequestStepperStatus(props) {
  return (
    <Gauge
      style={{ marginTop: 4 }}
      type="circle"
      value={
        (props.activeStepIndex + (props.activeStepPassed ? 1 : 0)) /
        props.stepsCount
      }
      size={30}
      bgColor={'#fff'}
      borderColor="#2196f3"
      borderWidth={2}
      valueText={
        <Button
          style={{
            textTransform: 'lowercase',
            fontSize: 16,
            fontFamily: 'monospace',
            color: '#2196f3'
          }}
          panelOpen="right"
        >
          i
        </Button>
      }
      valueFontSize={18}
      valueTextColor="#2196f3"
      labelText={''}
    />
  );
}

RequestStepperStatus.propTypes = {
  activeStepIndex: PropTypes.number,
  activeStepPassed: PropTypes.bool,
  stepsCount: PropTypes.number
};

export function RequestVerticalSteps(props) {
  return (
    <div className="stepper-container">
      {props.pagesInfo
        .filter(p => !p.summaryStep || !props.hideSummaryStep)
        .map((page, pageIndex) => (
          <Fragment key={pageIndex}>
            <div
              onClick={() =>
                page.readyToUse && props.goToRequestPage(pageIndex)
              }
              key={page.title + '_' + pageIndex}
              className={
                'step ' +
                `step_${pageIndex} ` +
                (page.hasProblems ? 'has-problem ' : '') +
                (page.readyToUse ? 'clickable ' : '') +
                (page.submitted ? 'submitted ' : '') +
                (page.done
                  ? page.visited
                    ? 'completed '
                    : 'pre-completed '
                  : '') +
                (pageIndex === props.activeStepIndex ? 'active ' : '') +
                (page.documents.length > 1 ? 'with-substeps ' : '')
              }
            >
              <div className="v-stepper">
                <div className="circle">
                  {page.done && page.visited ? (
                    <span className="material-icons">
                      {page.hasProblems ? 'priority_high' : 'done'}
                    </span>
                  ) : pageIndex === props.activeStepIndex ? (
                    <></>
                  ) : (
                    <></>
                  )}
                </div>
                <div className="line"></div>
              </div>

              <div className="content">{page.stepTitle || page.title}</div>
            </div>
            {(pageIndex === props.activeStepIndex ||
              props.pagesInfo.length === 2) &&
              page.documents.length > 1 &&
              page.documents.map((doc, docIndex) => (
                <div
                  onClick={() => props.navigateToDocument(doc, pageIndex)}
                  key={doc.name + '_' + docIndex}
                  className={
                    'stepDocumentAction_' +
                    doc.type +
                    ' step substep ' +
                    (page.docsWithProblems?.has(doc.id) ? 'has-problem ' : '') +
                    (doc.done ? 'completed ' : '') +
                    (doc.submitted ? 'submitted ' : '') +
                    (pageIndex === props.activeStepIndex ? 'selected ' : '') +
                    (doc.id === props.actionDocId ? 'active ' : '') +
                    (docIndex === page.documents.length - 1 ? 'last ' : '')
                  }
                >
                  <div className="v-stepper">
                    <div className="circle"></div>
                    <div className="line"></div>
                  </div>

                  <div className="content">{doc.name}</div>
                </div>
              ))}
          </Fragment>
        ))}
    </div>
  );
}

RequestVerticalSteps.propTypes = {
  hideSummaryStep: PropTypes.bool,
  navigateToDocument: PropTypes.func,
  activeStepIndex: PropTypes.number,
  pagesInfo: PropTypes.array,
  actionDocId: PropTypes.string,
  goToRequestPage: PropTypes.func
};

export function RequestStepperStatusPanel(props) {
  return (
    <Panel right floating id={'requestStepperStatusPanel'}>
      <Page>
        <RequestVerticalSteps {...props} />
      </Page>
    </Panel>
  );
}

RequestStepperStatusPanel.propTypes = {
  navigateToDocument: PropTypes.func,
  activeStepIndex: PropTypes.number,
  pagesInfo: PropTypes.array,
  actionDocId: PropTypes.string,
  goToRequestPage: PropTypes.func
};

export function RequestStepperNextButton(props) {
  const [clickedOnce, setClickedOnce] = React.useState(false);

  const mounted = React.useRef(false);

  React.useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (clickedOnce) {
      gaeUser('REQUEST_NEXT_BUTTON.clicked');
      mounted.current && props.onClick();
      setTimeout(() => mounted.current && setClickedOnce(false), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickedOnce]);

  return (
    <Fab
      id={'btnNextRequestAction'}
      className={'tinyFab iconOnRight ' + (props.className || '')}
      position="right-bottom"
      text={props.title || II18.get('title_next')}
      color={props.disabled ? 'gray' : 'blue'}
      onClick={() => !props.disabled && setClickedOnce(true)}
    >
      {!props.hideIcon && <Icon f7={'chevron_right_2'} />}
    </Fab>
  );
}

RequestStepperNextButton.propTypes = {
  title: PropTypes.string,
  className: PropTypes.string,
  hideIcon: PropTypes.bool,
  onClick: PropTypes.func,
  disabled: PropTypes.bool
};

export const sendTitleDefault = haveDocumentsToSend =>
  haveDocumentsToSend ? 'list_send_docs' : 'list_done';

export function SendButtonBlock(props) {
  const isTemplateNext = props.templateMode && !props.usersDataOk;
  const sendTitle = isTemplateNext
    ? 'title_next'
    : props.completeButtonLabel || sendTitleDefault(props.haveDocumentsToSend);

  const buttonActive =
    props.allRequiredOk && (props.privacyAccepted || props.templateMode);

  return (
    <Block
      className={props.className}
      style={{ display: 'flex', placeContent: 'center' }}
    >
      <div
        data-testid="sendButtonStatus"
        data-testvalue={buttonActive ? 'active' : 'notactive'}
      >
        {/*!props.templateMode && props.allRequiredOk && privacyCheckbox*/}
        <Button
          className={buttonActive ? 'active' : 'notactive'}
          id={'sendDocumentsButton'}
          raised
          fill
          style={{
            lineHeight: '48px',
            height: 48,
            fontSize: 16,
            padding: '0 30px',
            color: buttonActive ? 'white' : '#cfcfcf',
            backgroundColor: buttonActive ? 'green' : '#eae9e9',
            boxShadow: buttonActive ? {} : 'none'
          }}
          onClick={props.onClick}
        >
          <span data-testid="sendButtonTitle" data-testvalue={sendTitle}>
            <II18>{sendTitle}</II18>
          </span>
        </Button>
      </div>
    </Block>
  );
}

SendButtonBlock.propTypes = {
  className: PropTypes.string,
  templateMode: PropTypes.bool,
  haveDocumentsToSend: PropTypes.bool,
  allRequiredOk: PropTypes.bool,
  usersDataOk: PropTypes.bool,
  privacyAccepted: PropTypes.bool,
  completeButtonLabel: PropTypes.string,
  onClick: PropTypes.func
};

export function SecurityInfoBlock(props) {
  return (
    <div
      data-testid="securityInfo"
      style={{
        textAlign: 'center',
        padding: 5,
        marginTop: 10,
        paddingBottom: 25
      }}
    >
      <label style={{ fontSize: 13, color: '#8e8e93' }}>
        <Icon f7="lock" size={18} />{' '}
        <II18
          params={[
            props.companyName,
            II18.get(
              props.scanEnded
                ? 'home_security_info_part_may'
                : 'home_security_info_part_will'
            )
          ]}
          html
        >
          home_security_info
        </II18>
      </label>
    </div>
  );
}

SecurityInfoBlock.propTypes = {
  scanEnded: PropTypes.bool,
  companyName: PropTypes.string
};

export function ListHeaderBlock(props) {
  const title = props.onlyInfoDocuments
    ? 'list_title_got_documents'
    : props.scanEnded
    ? 'list_title_documents'
    : 'list_title_scan_documents';
  return (
    <BlockHeader>
      <span style={{ fontWeight: 500 }}>
        {!props.expired && (
          <span data-testid="listHeaderTitle" data-testvalue={title}>
            <II18>{props.pageTitle || title}</II18>
          </span>
        )}
      </span>
    </BlockHeader>
  );
}

ListHeaderBlock.propTypes = {
  scanEnded: PropTypes.bool,
  expired: PropTypes.bool,
  onlyInfoDocuments: PropTypes.bool,
  pageTitle: PropTypes.string
};

SecurityInfoBlock.propTypes = {
  onlyInfoDocuments: PropTypes.bool,
  scanEnded: PropTypes.bool,
  expired: PropTypes.bool
};

export function ExpiredWarningBlock() {
  return (
    <div
      data-testid="listExpiredWarningBlock"
      style={{
        display: 'flex',
        placeContent: 'center',
        padding: '30px 30px'
      }}
    >
      <div>
        <Icon size={40} material="timer_off" color="red" />
      </div>
      <div style={{ marginLeft: 20 }}>
        <span style={{ fontSize: window.isDesktop ? 16 : 14 }}>
          <II18>list_expired_warning_text</II18>
        </span>
      </div>
    </div>
  );
}

export function SuccessMessageBlock(props) {
  const sendDate = props.submittedDate
    ? props.submittedDate.replace(',', II18.get('list_success_message_at'))
    : '';

  return (
    <div data-testid="successMessageBlock" id="successMessageBlock">
      <div>
        <Icon size={40} material="done_all" color="#0B7BE9" />
      </div>
      <div
        style={{
          lineHeight: window.isDesktop ? '40px' : 'normal',
          marginLeft: 20
        }}
      >
        <span style={{ fontSize: window.isDesktop ? 16 : 14 }}>
          {II18.get(
            props.oneDocument
              ? 'document_success_message'
              : 'list_success_message',
            sendDate
          )}
        </span>
      </div>
    </div>
  );
}

SuccessMessageBlock.propTypes = {
  submittedDate: PropTypes.string,
  oneDocument: PropTypes.bool
};

export function prepareListInfo(
  document,
  scannedPages,
  forms,
  documentsStatus,
  successSentKeys,
  errorSentKeys,
  scanEnded
) {
  const hasScannedPages = scannedPages && scannedPages.size;

  const documentReady = document.done;
  const docType = document.type;
  const docStats = document.stats;
  const docStatus = documentsStatus.get(document.id);

  let badge = parseInt(hasScannedPages);
  const hasProblem = isDocumentHasProblems(
    errorSentKeys,
    document,
    scannedPages
  );

  const submittedStatus = document.submitted;
  const rejected = document.rejected;

  const viewOnlyMode =
    scanEnded ||
    submittedStatus ||
    rejected ||
    document.isFormEditable === false ||
    document.isOnlyAcceptButton ||
    document.isOnlySignatures;

  const submitted =
    submittedStatus ||
    (docType === DOC_TYPE_PAYMENT && documentsStatus.has(document.id));

  let mobileIcon = {
    md: hasScannedPages ? 'material:collections' : 'material:photo_camera',
    ios: hasScannedPages ? 'f7:rectangle_stack' : 'f7:camera_circle'
  };

  let desctopIconRight = submittedStatus
    ? 'material:check'
    : hasScannedPages
    ? 'material:view_module'
    : 'material:add_box';

  let desctopTitle = II18.get(
    submittedStatus || rejected
      ? 'title_see'
      : hasScannedPages
      ? 'title_see'
      : 'upload_disk'
  );

  const okBadge = true;

  switch (docType) {
    case DOC_TYPE_INFORMATION:
      mobileIcon = {
        md: 'material:info',
        ios: 'f7:info_circle'
      };
      desctopIconRight = 'material:info';
      desctopTitle = II18.get('title_read');
      badge = docStats?.downloadRequired
        ? docStatus?.downloaded && okBadge
        : '';
      break;

    case DOC_TYPE_IMMEDIATE_CONFIRMATION:
      mobileIcon = {
        md: 'material:assignment_late',
        ios: 'material:assignment_late'
      };
      desctopIconRight = 'material:assignment_late';
      desctopTitle = II18.get(
        viewOnlyMode ? 'title_read' : 'title_read_and_accept'
      );
      badge = documentReady && okBadge;
      break;

    case DOC_TYPE_ID_CHECK:
      {
        mobileIcon = {
          md: 'material:assignment_ind',
          ios: 'f7:person_alt_circle'
        };
        desctopIconRight = 'fa-user-check';
        desctopTitle = II18.get(viewOnlyMode ? 'title_see' : 'title_execute');
        // eslint-disable-next-line no-case-declarations
        const idCheckStatus = prepareIdCheckSteps(
          document,
          scannedPages,
          documentsStatus.get(document.id)
        );
        const doneSteps = idCheckStatus.filter(step => step.passed).length;
        badge = documentReady
          ? okBadge
          : doneSteps
          ? doneSteps + '/' + idCheckStatus.length
          : '';
      }
      break;

    case DOC_TYPE_FORM:
      mobileIcon = {
        md: 'material:assignment',
        ios: 'f7:doc_plaintext'
      };

      desctopIconRight = 'fa-keyboard';
      desctopTitle = II18.get(
        viewOnlyMode
          ? 'title_see'
          : documentReady
          ? 'title_change'
          : document.isOnlySignatures
          ? 'title_see'
          : 'title_fill'
      );
      badge = documentReady ? okBadge : '';

      break;

    case DOC_TYPE_PDF_FORM:
      mobileIcon = {
        md: 'material:assignment',
        ios: 'f7:doc_plaintext'
      };

      desctopIconRight = 'fa-keyboard';
      desctopTitle = II18.get(
        viewOnlyMode
          ? 'title_see'
          : documentReady
          ? 'title_change'
          : document.isOnlySignatures
          ? 'title_see'
          : 'title_fill'
      );
      badge = documentReady && document.isFormEditable ? okBadge : '';

      break;

    case DOC_TYPE_PAYMENT:
      mobileIcon = {
        md: 'material:local_atm',
        ios: 'f7:money_euro_circle'
      };

      desctopIconRight = 'fa-hand-holding-usd';
      desctopTitle = II18.get(documentReady ? 'title_see' : 'payment_do_pay');

      badge = documentReady ? okBadge : '';

      break;
    //no default
  }

  badge = submitted ? '' : badge ? badge : '';
  if (rejected) {
    badge = <Icon material={'block'} size={20} />;
  }

  mobileIcon = {
    md: submitted && !rejected ? 'material:check' : mobileIcon.md,
    ios: submitted && !rejected ? 'f7:checkmark_alt_circle' : mobileIcon.ios
  };

  const desctopIcon =
    submitted && !rejected
      ? getIcon('f7:checkmark_alt', 40)
      : getDocumentTypeIcon(docType);

  return {
    mobileIcon,
    desctopIcon,
    desctopIconRight,
    desctopTitle,
    hasScannedPages,
    documentReady,
    badge,
    submitted,
    hasProblem,
    rejected
  };
}

export function InPageContentTitle(props) {
  return (
    <div className="document-page-header">
      {props.contentIndex !== undefined && (
        <span className={'stepNumber'}>{props.contentIndex}</span>
      )}
      {props.contentIcon && props.contentIcon}
      <label>{props.contentTitle}</label>
    </div>
  );
}

InPageContentTitle.propTypes = {
  style: PropTypes.object,
  contentIndex: PropTypes.number,
  contentTitle: PropTypes.string,
  contentIcon: PropTypes.any
};

export function StepperInPageContent(props) {
  return (
    <div className="documents-page document-page-container" style={props.style}>
      <div className="common-page-content">
        <div className="document-page-body tinny-scrollbar">
          {props.content}
        </div>
      </div>
    </div>
  );
}

StepperInPageContent.propTypes = {
  style: PropTypes.object,
  contentIndex: PropTypes.number,
  contentTitle: PropTypes.string,
  content: PropTypes.any,
  contentIcon: PropTypes.any
};

export function StartContent(props) {
  return (
    <div className={'start-content'} style={{ padding: 20 }}>
      <Row
        className={'companyInfo'}
        style={{
          justifyContent: 'flex-start',
          gap: 15,
          borderRadius: 10,
          padding: 10
        }}
      >
        <Col style={{ height: 65, width: 70 }}>
          <img
            alt={props.companyName || 'Paperfly'}
            src={props.companyLogo || '/favicon512.png'}
            width="60"
            height="60"
            style={{
              borderRadius: 5,
              border: '2px solid #d5dee6',
              background: '#fff'
            }}
          />
        </Col>
        <Col style={{ flex: 1 }}>
          <Row>
            <span
              data-testid="cardCompanyName"
              style={{
                fontSize: 24,
                fontWeight: 500,
                marginTop: 0
              }}
            >
              {props.companyName || 'Paperfly'}
            </span>
          </Row>
          <Row>
            <span
              data-testid="cardCaseName"
              style={{ fontSize: 18, opacity: 0.9 }}
            >
              {props.caseName}
            </span>
          </Row>
        </Col>
      </Row>
      <div style={{ borderTop: '1px solid #eee', margin: 15 }} />
      <div
        dangerouslySetInnerHTML={{
          __html: props.requestMessage
        }}
      />
    </div>
  );
}

StartContent.propTypes = {
  companyName: PropTypes.string,
  caseName: PropTypes.string,
  companyLogo: PropTypes.string,
  requestMessage: PropTypes.string
};
